import { useState } from 'react'
import {
  Box,
  Text,
  Columns,
  Column,
  Stack,
  Bleed,
  Inline,
  Divider,
  Button,
  Alert,
  Hidden,
  useToast
} from '@machobear/design-system'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldIcon } from '@/components/icons'
import { add, remove } from '@/models/plans/apis'
import useSession from '@/hooks/use-current-session'
import useBasic from '@/hooks/use-basic'
import { validNumber, validExpirationDate, validCvv } from '@/utils/payments'
import { getFormattedStates } from '@/utils/address'
import { AddingPaymentMethod } from '../ds-checkout-modal/types'
import { FieldInput, Dropdown } from '../ds-form'

type Props = {
  closeModal: () => void
  formInitialValues: any
}

const EditPaymentModalContent = ({ closeModal, formInitialValues }: Props) => {
  const { t } = useTranslation()
  const showToast = useToast()
  const [validCVCLength, setValidCVCLength] = useState(null)
  const session = useSession()
  const showWarning = session?.company_info?.base_plan_name !== 'essentials-plan-monthly'

  const { countries }: any = useBasic(['countries'])
  const formattedStates = getFormattedStates(countries, 'ca')

  const client = useQueryClient()

  const addMethod = useMutation({
    mutationFn: (values: AddingPaymentMethod) => add(values)
  })

  const deletePaymentMethod = useMutation({
    mutationFn: remove
  })

  const onSubmit = (values: any, { setSubmitting }: any) => {
    setSubmitting(true)
    deletePaymentMethod.mutate(formInitialValues?.payment_method_id || '', {
      onSuccess: async () => {
        addMethod.mutate(
          {
            ...values,
            card_number: values.card_number.replace(/\s/g, ''),
            expiration_date: values.expiration_date.replace('/', '')
          },
          {
            onSuccess: async () => {
              client.invalidateQueries({ queryKey: ['payment-methods'] })
              showToast({
                message: t('b.payment.edit.payment_method.success', 'Payment method updated!'),
                tone: 'positive'
              })
              setSubmitting(false)
              closeModal()
            }
          }
        )
      }
    })
  }

  const acceptedCards = ['visa', 'mastercard', 'american-express', 'unionpay']

  const isCardNumberValid = (cardNumber: string) => {
    const { card, isValid }: any = validNumber(cardNumber)
    setValidCVCLength(card ? card?.code.size : null)
    return acceptedCards.includes(card?.type) && isValid
  }

  const isExpirationValid = (expiration: string) => {
    const { isValid: isExpValid } = validExpirationDate(expiration)
    return isExpValid
  }

  const isSecurityCodeValid = (securityCode: string) => {
    if (validCVCLength) {
      const { isValid: isCodeValid } = validCvv(securityCode, validCVCLength)
      return isCodeValid
    }
    return true
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required()
      .matches(/^[aA-zZ\s]+$/, 'Can only contain letters'),
    last_name: Yup.string()
      .required()
      .matches(/^[aA-zZ\s]+$/, 'Can only contain letters'),
    street1: Yup.string().required(),
    street2: Yup.string(),
    city: Yup.string().required(),
    state: Yup.string().required(),
    country: Yup.string().required(),
    zip: Yup.string().required(),
    card_number: Yup.string()
      .required()
      .test(
        'valid card',
        t('r.payment.edit.invalid_card_number.error', 'Invalid card number'),
        value => isCardNumberValid(value ? value : '')
      ),
    expiration_date: Yup.string()
      .required()
      .test('valid expiration', t('b.checkout.error.expiry', 'Invalid date'), value =>
        isExpirationValid(value ? value : '')
      ),
    security_code: Yup.string()
      .required()
      .min(3, t('b.checkout.error.cvc', 'Invalid CVC'))
      .test('cvc', t('b.checkout.error.cvc', 'Invalid CVC'), value =>
        isSecurityCodeValid(value ? value : '')
      ),

    default_payment_method: Yup.boolean(),
    save_payment_method: Yup.boolean(),
    save_billing_address: Yup.boolean()
  })

  const initialValues = {
    first_name: formInitialValues?.first_name || '',
    last_name: formInitialValues?.last_name || '',
    card_number: '',
    expiration_date: formInitialValues?.expiration_date || '',
    security_code: '',
    street1: formInitialValues?.street1 || '',
    street2: formInitialValues?.street2 || '',
    city: formInitialValues?.city || '',
    state: formInitialValues?.state || '',
    country: formInitialValues?.country.toLowerCase() || '',
    zip: formInitialValues?.zip || '',
    default_payment_method: true,
    save_payment_method: true,
    save_billing_address: true,
    payment_method: 'stripe'
  }

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
      {({ isSubmitting }) => {
        return (
          <Form>
            <Stack space="gutter">
              {showWarning && (
                <Alert tone="caution">
                  <Text size="small">
                    This will also replace your current payment method for any active subscription
                    plans after you submit your purchase.
                  </Text>
                </Alert>
              )}

              <Stack space="small">
                <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                  <Text>{t('b.checkout.title.credit_card_details', 'Credit card details')}</Text>

                  <Box display="flex" alignItems="center" gap="xxxsmall">
                    <ShieldIcon width={16} height={16} />
                    <Text tone="tertiary" size="xsmall">
                      {t('b.checkout.title.secure_ecryption', 'Secure Encryption')}
                    </Text>
                  </Box>
                </Box>
                <Columns space="small">
                  <Column>
                    <FieldInput
                      name="first_name"
                      label={t('b.checkout.field.first_name', 'First name')}
                      required
                    />
                  </Column>

                  <Column>
                    <FieldInput
                      name="last_name"
                      label={t('b.checkout.field.last_name', 'Last name')}
                      required
                    />
                  </Column>
                </Columns>
                <Box
                  gap="xsmall"
                  display="flex"
                  flexDirection={{ desktop: 'row', mobile: 'column' }}
                >
                  <Column width="3/5">
                    <FieldInput
                      name="card_number"
                      maxLength={19}
                      mask={(value: string) =>
                        value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ')
                      }
                      label={t('b.checkout.field.card_number', 'Card number')}
                      required
                    />
                  </Column>

                  <Columns space="xsmall">
                    <Column width="1/2">
                      <FieldInput
                        name="expiration_date"
                        mask={(value: string) =>
                          value.replace(/\D/g, '').replace(/(\d{2})(?=\d)/g, '$1/')
                        }
                        label={t('b.checkout.field.expiry', 'MM/YY')}
                        maxLength={5}
                        required
                      />
                    </Column>
                    <Column width="1/2">
                      <FieldInput
                        name="security_code"
                        label={t('b.checkout.field.cvc', 'CVC')}
                        maxLength={4}
                        required
                      />
                    </Column>
                  </Columns>
                </Box>
              </Stack>

              <Stack space="small">
                <Text>{t('b.checkout.title.billing_address', 'Billing Address')}</Text>
                <Columns space="xsmall">
                  <Column>
                    <FieldInput
                      name="street1"
                      label={t('b.checkout.field.address', 'Street address')}
                      required
                    />
                  </Column>

                  <Column>
                    <FieldInput
                      name="street2"
                      label={t('b.checkout.field.address_2', 'Suite no./ Unit no.')}
                    />
                  </Column>
                </Columns>
                <Columns space="xsmall">
                  <Column>
                    <FieldInput name="city" label={t('b.checkout.field.city', 'City')} required />
                  </Column>

                  <Column>
                    <Dropdown
                      name="state"
                      label={t('b.checkout.field.province', 'Province')}
                      required
                      options={formattedStates}
                    />
                  </Column>
                </Columns>
                <Columns space="xsmall">
                  <Column>
                    <Dropdown
                      name="country"
                      label={t('b.checkout.field.country', 'Country')}
                      required
                      disabled
                      options={[{ value: 'ca', label: 'Canada' }]}
                    />
                  </Column>

                  <Column>
                    <FieldInput
                      name="zip"
                      label={t('b.checkout.field.zip', 'Postal code')}
                      required
                    />
                  </Column>
                </Columns>
              </Stack>

              <Bleed horizontal="large">
                <Divider />
              </Bleed>

              <Hidden below="tablet">
                <Inline space="xsmall" align="right" alignY="center">
                  <Button variant="transparent" onClick={closeModal} disabled={isSubmitting}>
                    {t('common.back', 'Back')}
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? t('common.loading', 'Loading...') : t('common.save', 'Save')}
                  </Button>
                </Inline>
              </Hidden>
              <Hidden above="tablet">
                <Box display="flex" alignItems="center" gap="xsmall">
                  <Button variant="transparent" onClick={closeModal} disabled={isSubmitting}>
                    {t('common.back', 'Back')}
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? t('common.loading', 'Loading...') : t('common.save', 'Save')}
                  </Button>
                </Box>
              </Hidden>
            </Stack>
          </Form>
        )
      }}
    </Formik>
  )
}

export default EditPaymentModalContent
